import { useTranslation } from "react-i18next";
import { ScrollReveal } from "@/components/motion/ScrollReveal";

/**
 * StoryIntro — Part 1 of the home-page narrative triptych.
 *
 * Renders an editorial preamble (eyebrow + h2 + three approved paragraphs)
 * that positions the 2026 edition before the launch-video moment beneath.
 * Copy is sourced verbatim from `data.ts` (single source of truth).
 *
 * Layout:
 *   - Single column, `max-w-3xl mx-auto`, generous vertical breathing room.
 *   - Thin race-coloured trim above the eyebrow signals "narrative starts here".
 *   - First paragraph is treated as the lead (slightly larger / heavier weight).
 *
 * The whole block fades in once when it enters the viewport; the three
 * paragraphs cascade with a small stagger so the section reads top-down.
 */
export function StoryIntro() {
  const { t } = useTranslation("home");
  const eyebrow = t("story.eyebrow");
  const heading = t("story.heading");
  const paragraphs = t("story.paragraphs", { returnObjects: true }) as string[];
  const [leadParagraph, ...restParagraphs] = paragraphs;

  return (
    <section
      aria-labelledby="story-intro-heading"
      className="relative bg-background"
    >
      <div className="container pt-10 pb-20 md:pt-14 md:pb-28">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-xs uppercase tracking-[0.28em] text-primary font-semibold">
              {eyebrow}
            </p>
            <h2
              id="story-intro-heading"
              className="mt-4 font-display text-4xl md:text-6xl leading-[0.95] tracking-normal text-balance"
            >
              {heading}
            </h2>
          </ScrollReveal>

          <ScrollReveal.Group className="mt-10 space-y-6" stagger={0.08}>
            {/* First paragraph rendered as the lead. */}
            <ScrollReveal.Item>
              <p className="text-xl md:text-2xl leading-relaxed text-foreground/85 font-medium">
                {leadParagraph}
              </p>
            </ScrollReveal.Item>

            {restParagraphs.map((paragraph, idx) => (
              <ScrollReveal.Item key={idx}>
                <p className="text-lg md:text-xl leading-relaxed text-foreground/75">
                  {paragraph}
                </p>
              </ScrollReveal.Item>
            ))}
          </ScrollReveal.Group>
        </div>
      </div>
    </section>
  );
}
